import request from '../../utils/request';
import { Result } from '@/models/general';
import {
  TournamentView,
  TournamentListView,
  TournamentStatsView,
  TournamentListQuery,
  CreateTournamentInput,
  UpdateTournamentInput,
  StandingEntry,
  BracketView,
  GenerateBracketInput,
  TournamentDeletionPreviewView,
  TournamentDeletionResultView,
  DeleteTournamentInput,
} from '@/models/tournament';

export async function getTournaments(query: TournamentListQuery) {
  const response = await request.get<Result<TournamentListView>>('Tournament/GetTournaments', { params: query });
  return response.data;
}

/** Server clamps `PageSize` to 1..100 (`TournamentService.GetTournaments`). Asking for more is silently capped. */
const TOURNAMENT_PAGE_SIZE = 100;
/** Runaway guard. */
const TOURNAMENT_MAX_PAGES = 100;

/**
 * Every tournament, not just the first server page. Same clamp trap as `getRefereesAllPages`:
 * pickers that passed `pageSize: 200` were silently capped at 100.
 */
export async function getTournamentsAllPages(query: Omit<TournamentListQuery, 'page' | 'pageSize'> = {}) {
  const first = await getTournaments({ ...query, page: 1, pageSize: TOURNAMENT_PAGE_SIZE });
  if (first.errors.length > 0 || !first.payload) return first;

  const items = [...first.payload.items];
  const totalPages = Math.min(first.payload.totalPages ?? 1, TOURNAMENT_MAX_PAGES);

  for (let page = 2; page <= totalPages; page++) {
    const next = await getTournaments({ ...query, page, pageSize: TOURNAMENT_PAGE_SIZE });
    if (next.errors.length > 0 || !next.payload) return next; // surface the failure, never a half list
    if (next.payload.items.length === 0) break;
    items.push(...next.payload.items);
  }

  return {
    ...first,
    payload: { ...first.payload, items, page: 1, pageSize: items.length },
  };
}

export async function getTournament(id: number) {
  const response = await request.get<Result<TournamentView>>(`Tournament/GetTournament/${id}`);
  return response.data;
}

export async function createTournament(input: CreateTournamentInput) {
  const response = await request.post<Result<TournamentView>>('Tournament/CreateTournament', input);
  return response.data;
}

export async function updateTournament(input: UpdateTournamentInput) {
  const response = await request.put<Result<TournamentView>>('Tournament/UpdateTournament', input);
  return response.data;
}

export async function archiveTournament(id: number) {
  const response = await request.post<Result<boolean>>(`Tournament/ArchiveTournament/${id}`);
  return response.data;
}

/** CO-922: what an HR cascade delete would remove, keep and claw back (HrOnly; archived tournaments too). */
export async function getTournamentDeletePreview(id: number) {
  const response = await request.get<Result<TournamentDeletionPreviewView>>(`Tournament/DeletePreview/${id}`);
  return response.data;
}

/**
 * CO-922: permanent cascade delete (HrOnly). The interceptor rejects every non-2xx, but a 400/409 here
 * carries a structured `Result` (error code, and on 409 a fresh preview in `payload.preview`) that the
 * dialog renders, so a rejected Result body is handed back as the value. Anything else still throws.
 */
export async function deleteTournamentPermanently(id: number, input: DeleteTournamentInput) {
  try {
    const response = await request.post<Result<TournamentDeletionResultView>>(`Tournament/DeleteTournament/${id}`, input);
    return response.data;
  } catch (err) {
    const body = err as Result<TournamentDeletionResultView> | null;
    if (body && typeof body === 'object' && Array.isArray((body as { errors?: unknown }).errors)) return body;
    throw err;
  }
}

export async function getTournamentStats() {
  const response = await request.get<Result<TournamentStatsView>>('Tournament/GetTournamentStats');
  return response.data;
}

export async function getStandings(tournamentId: number) {
  const response = await request.get<Result<StandingEntry[]>>(`Tournament/GetStandings/${tournamentId}`);
  return response.data;
}

export async function getBracket(tournamentId: number) {
  const response = await request.get<Result<BracketView>>(`Tournament/GetBracket/${tournamentId}`);
  return response.data;
}

export async function generateBracket(tournamentId: number, input: GenerateBracketInput) {
  const response = await request.post<Result<BracketView>>(`Tournament/GenerateBracket/${tournamentId}`, input);
  return response.data;
}
